import { User } from '@choco-admin/types';
import { useCallback } from 'react';

import { useUsersPageUpdateUserMutation } from '../../../generated';
import { mapToUser } from '../utils/userMapper';

export type CaptureExceptionFunction = (error: Error) => void;

export type UpdateUserInput = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  locale?: string;
  isTest: boolean;
  supplier: boolean;
  supportLogin?: boolean;
  orderPreferences?: {
    pdf?: boolean;
    xlsx?: boolean;
    sms?: string | null;
    whatsApp?: string | null;
  };
};

export type UseUpdateUserResponse = {
  updateUser: (input: UpdateUserInput) => Promise<User>;
  isLoading: boolean;
};

export type UseUpdateUserConfig = {
  captureException?: CaptureExceptionFunction;
};

export const useUpdateUser = (
  config: UseUpdateUserConfig
): UseUpdateUserResponse => {
  const { captureException } = config;

  const [updateUserMutation, { loading }] = useUsersPageUpdateUserMutation();

  const updateUser = useCallback(
    async (input: UpdateUserInput): Promise<User> => {
      try {
        const result = await updateUserMutation({
          variables: {
            userUpdateInput: {
              id: input.id,
              name: input.name,
              email: input.email,
              phone: input.phone,
              locale: input.locale,
              isTest: input.isTest,
              supplier: input.supplier,
              chocoUser: input.supportLogin,
              orderPreference: input.orderPreferences,
            },
          },
          update: (cache, { data: mutationData }) => {
            if (mutationData?.userUpdate) {
              cache.modify({
                fields: {
                  userSearch: (existing) => {
                    return existing;
                  },
                },
              });
            }
          },
        });

        if (!result.data?.userUpdate) {
          throw new Error('Failed to update user');
        }

        const updatedUser = result.data.userUpdate;

        // Map to generic User type using shared mapper
        return mapToUser(updatedUser);
      } catch (error) {
        captureException?.(error as Error);
        throw error;
      }
    },
    [updateUserMutation, captureException]
  );

  return {
    updateUser,
    isLoading: loading,
  };
};
